import { FC } from 'react';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { NotificationsUtils } from '@/features/notifications/utils/notificationsUtils';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { t } from 'i18next';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import NotificationsListEmpty from '@/features/notifications/componentns/NotificationsList/NotificationsListEmpty';
import {
	AdminNotificationsListItemResponseDto,
	AdminNotificationsListResponseDto,
} from '@/features/notifications/types/notifications-dto';
import { ETablePaginationWithApi } from '@/components/everridge/table/components/ETablePaginationWithApi';
import { format } from 'date-fns';

type Props = {
	data?: AdminNotificationsListResponseDto;
	isLoading?: boolean;
	page: number;
	setPage: (page: number) => void;
	paginationStep?: number;
	showDetails?: (id: string) => void;
};

const NotificationsAdminList: FC<Props> = ({
	page,
	setPage,
	paginationStep = 10,
	data,
	isLoading,
	showDetails,
}) => {
	const groupedActivities =
		data?.items?.reduce((acc, curr) => {
			const dateKey = format(new Date(curr.sent), 'yyyy-MM-dd');

			if (!acc[dateKey]) {
				acc[dateKey] = [];
			}
			acc[dateKey].push(curr);

			return acc;
		}, {} as Record<string, AdminNotificationsListItemResponseDto[]>) || [];

	return (
		<div className='p-0 flex-1 flex flex-col gap-4 rounded-2.5xl bg-popover-30 select-none'>
			<div className='flex-1'>
				{isLoading && <EActivitiesListSkeleton isMobile={true} />}
				{!isLoading && !!data?.items?.length && (
					<div className='flex flex-col gap-4'>
						{Object.entries(groupedActivities).map(
							([date, notifications]) => {
								return (
									<div key={`${date}`}>
										<h5 className='text-sm text-popover-800'>
											{date}
										</h5>
										{notifications?.map((notification) => {
											const { label, icon, iconColors } =
												NotificationsUtils.getNotificationsConfig(
													notification.category,
												);

											const header = `${label} ${
												notification.isRequired
													? ''
													: '(optional)'
											}`;

											return (
												<EActivitiesElement
													key={notification.id}
													icon={icon}
													colorClass={iconColors}
													header={header}
													description={
														notification.message ??
														t(
															'main.notifications.components.list.noTitle',
														)
													}
													value={DateTimeHelper.formatTime(
														notification.sent,
													)}
													onClick={showDetails}
													id={notification.id}
													isMobile={true}
												/>
											);
										})}
									</div>
								);
							},
						)}
					</div>
				)}
				{!isLoading && !data?.items?.length && (
					<NotificationsListEmpty />
				)}
			</div>
			{!!data?.totalCount && data?.totalCount > 10 && (
				<ETablePaginationWithApi
					totalCount={Math.ceil(data?.totalCount / paginationStep)}
					page={page}
					setPage={setPage}
				/>
			)}
		</div>
	);
};

export default NotificationsAdminList;
